import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import Stack from '@mui/system/Stack/Stack';
import { useState, useEffect } from 'react';
import { primaryDark, secondaryBlue } from '../../../../utils/color';
import { APPBAR_HEIGHT } from '../../../../layout/dashboard-navigation/NavigationDashboard';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/icons/project-icons/ArrowRightIcon.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../../assets/icons/layout-icons/ArrowLeftShortFill.svg';
import AiChatDisclaimer from '../../../Chat/AiChatDisclaimer';
import axios from '../../../../api/axios';
import useAuth from '../../../../utils/hooks/useAuth';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface Suggestion {
  id: string;
  label: string;
  prompt: string;
}

interface RegistrationAssistantSidebarProps {
  currentStep: number;
  userType?: string;
  sessions?: any[];
}

const stepSuggestions: Suggestion[] = [
  {
    id: '1',
    label: 'Core Business',
    prompt:
      "What is the company's core business and main areas of expertise based on the collected description?",
  },
  {
    id: '2',
    label: 'Industry Focus',
    prompt:
      'Which industries does the company primarily serve, and how diverse is its industry focus?',
  },
  {
    id: '3',
    label: 'Service Offering',
    prompt:
      'What services does the company offer, and how would you categorize them (e.g. software, consulting, retail, manufacturing)?',
  },
  {
    id: '4',
    label: 'Market Presence',
    prompt:
      'Where is the company headquartered, and which geographic markets does it mainly target?',
  },
  {
    id: '5',
    label: 'Missing Data',
    prompt:
      'Which important company details are missing or incomplete and may require manual input or confirmation?',
  },
];

const SIDEBAR_WIDTH = 420;
const COLLAPSED_WIDTH = 48;

const RegistrationAssistantSidebar = ({
  currentStep,
  userType,
  sessions,
}: RegistrationAssistantSidebarProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content:
        "Welcome to the Spona company registration process. This setup will collect your company details in four short steps.\n\nYou can ask me to generate content for any field or clarify any field's purpose.",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchedSessionId, setLastFetchedSessionId] = useState<string | null>(null);

  const {
    authData: { userData },
  } = useAuth();

  // Filter sessions by kind and non-null context
  const registrationSessions =
    sessions?.filter((session) => session.kind === 'post_registration_assistant') || [];

  // Get suggestions
  const currentSuggestions = stepSuggestions;
  const displayedSuggestions = showAllSuggestions
    ? currentSuggestions
    : currentSuggestions.slice(0, 3);

  // Fetch previous messages from session
  useEffect(() => {
    const fetchMessages = async () => {
      if (!registrationSessions || registrationSessions.length === 0 || !userData?.id) return;

      const sessionId = registrationSessions[registrationSessions.length - 1].id;

      // Skip if we already fetched messages for this session
      if (lastFetchedSessionId === sessionId) return;

      try {
        const response = await axios.get(`/ai/sessions/${sessionId}/messages`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': String(userData.id),
          },
        });

        if (response.data && response.data.length > 0) {
          const fetchedMessages: Message[] = response.data.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          }));

          // Set messages with welcome message first, then fetched messages
          setMessages([
            {
              id: '1',
              role: 'assistant',
              content:
                "Welcome to the Spona company registration process. This setup will collect your company details in four short steps.\n\nYou can ask me to generate content for any field or clarify any field's purpose.",
              timestamp: new Date(),
            },
            ...fetchedMessages,
          ]);
        }

        // Mark this session as fetched
        setLastFetchedSessionId(sessionId);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    fetchMessages();
  }, [registrationSessions?.length, userData?.id, lastFetchedSessionId]);

  // Reset suggestions view when step changes
  useEffect(() => {
    setShowAllSuggestions(false);
  }, [currentStep]);

  const handleSuggestionClick = async (suggestion: Suggestion) => {
    if (!registrationSessions || registrationSessions.length === 0 || !userData?.id) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: suggestion.prompt,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        '/ai/messages',
        {
          content: suggestion.prompt,
          session_id: Number(registrationSessions[registrationSessions.length - 1].id),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': String(userData.id),
          },
        },
      );

      const aiMessage: Message = {
        id: response.data[1].id,
        role: 'assistant',
        content: response.data[1].content,
        timestamp: new Date(response.data[1].created_at),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!registrationSessions || registrationSessions.length === 0 || !userData?.id) return;
    if (!inputValue.trim()) return;

    const messageContent = inputValue.trim();
    setInputValue('');

    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        '/ai/messages',
        {
          content: messageContent,
          session_id: Number(registrationSessions[registrationSessions.length - 1].id),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': String(userData.id),
          },
        },
      );

      const aiMessage: Message = {
        id: response.data[1].id,
        role: 'assistant',
        content: response.data[1].content,
        timestamp: new Date(response.data[1].created_at),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={{
        width: isCollapsed ? `${COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px`,
        height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
        position: 'fixed',
        right: 0,
        top: `${APPBAR_HEIGHT}px`,
        bgcolor: 'white',
        borderLeft: `1px solid ${secondaryBlue[100]}`,
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        overflow: 'hidden',
      }}
    >
      {!isCollapsed && (
        <>
          <Stack
            sx={{
              p: 3,
              borderBottom: `1px solid ${secondaryBlue[100]}`,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="heading3"
              sx={{
                color: primaryDark[600],
                fontFamily: 'Gellix-SemiBold',
              }}
            >
              AI REGISTRATION ASSISTANT
            </Typography>
            <IconButton
              onClick={() => setIsCollapsed(true)}
              sx={{
                width: 32,
                height: 32,
                color: primaryDark[500],
                '&:hover': {
                  bgcolor: 'primaryLight.50',
                },
              }}
            >
              <ArrowRightIcon fill={primaryDark[500]} />
            </IconButton>
          </Stack>
          <AiChatDisclaimer />

          <Box
            sx={{
              flex: 1,
              p: 3,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: message.role === 'user' ? secondaryBlue[500] : 'primaryLight.50',
                    color: message.role === 'user' ? 'white' : primaryDark[600],
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {message.content}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: primaryDark[400],
                    mt: 0.5,
                    ml: message.role === 'user' ? 0 : 1,
                    mr: message.role === 'user' ? 1 : 0,
                    textAlign: message.role === 'user' ? 'right' : 'left',
                  }}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            ))}

            {isLoading && (
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: 'primaryLight.50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: primaryDark[500] }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: primaryDark[600],
                    }}
                  >
                    Thinking...
                  </Typography>
                </Box>
              </Box>
            )}

            {currentSuggestions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: primaryDark[500],
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Suggestions
                </Typography>
                <Stack spacing={1}>
                  {displayedSuggestions.map((suggestion) => (
                    <Chip
                      key={suggestion.id}
                      label={suggestion.label}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        justifyContent: 'flex-start',
                        height: 'auto',
                        minHeight: '32px',
                        py: 1,
                        px: 1.5,
                        fontSize: '13px',
                        fontWeight: 500,
                        bgcolor: 'primaryLight.50',
                        color: primaryDark[600],
                        border: `1px solid ${secondaryBlue[100]}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: secondaryBlue[50],
                          borderColor: secondaryBlue[500],
                        },
                        '& .MuiChip-label': {
                          padding: 0,
                          whiteSpace: 'normal',
                          textAlign: 'left',
                        },
                      }}
                    />
                  ))}
                  {currentSuggestions.length > 3 && !showAllSuggestions && (
                    <Button
                      onClick={() => setShowAllSuggestions(true)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: secondaryBlue[500],
                        justifyContent: 'flex-start',
                        px: 1.5,
                        '&:hover': {
                          bgcolor: 'primaryLight.50',
                        },
                      }}
                    >
                      Show more ({currentSuggestions.length - 3})
                    </Button>
                  )}
                  {showAllSuggestions && currentSuggestions.length > 3 && (
                    <Button
                      onClick={() => setShowAllSuggestions(false)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: secondaryBlue[500],
                        justifyContent: 'flex-start',
                        px: 1.5,
                        '&:hover': {
                          bgcolor: 'primaryLight.50',
                        },
                      }}
                    >
                      Show less
                    </Button>
                  )}
                </Stack>
              </Box>
            )}
          </Box>

          <Stack
            sx={{
              p: 2,
              borderTop: `1px solid ${secondaryBlue[100]}`,
              gap: 1,
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                fullWidth
                multiline
                maxRows={3}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '13px',
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                sx={{
                  bgcolor: secondaryBlue[500],
                  color: 'white',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    bgcolor: secondaryBlue[700],
                  },
                  '&.Mui-disabled': {
                    bgcolor: primaryDark[200],
                    color: primaryDark[400],
                  },
                }}
              >
                <ArrowRightIcon fill="#fff" />
              </IconButton>
            </Stack>
          </Stack>
        </>
      )}

      {isCollapsed && (
        <Stack
          sx={{
            height: '100%',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 3,
          }}
        >
          <IconButton
            onClick={() => setIsCollapsed(false)}
            sx={{
              width: 40,
              height: 40,
              color: primaryDark[500],
              '&:hover': {
                bgcolor: 'primaryLight.50',
              },
            }}
          >
            <ArrowLeftIcon fill={primaryDark[500]} />
          </IconButton>
          <Typography
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              mt: 3,
              fontSize: '12px',
              fontWeight: 600,
              color: primaryDark[400],
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            AI Assistant
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default RegistrationAssistantSidebar;
